import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { crmDeepLink } from '@/lib/links.js';
import { api } from '@/lib/api.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import MoveConfirm from '@/components/money/MoveConfirm.jsx';
import { fmtCents, fmtPct } from '@/components/money/format.js';

// THE SWEEP QUEUE (TRACON-SWEEP-QUEUE-SPEC §3, TRACON-MONEY-SPEC §6).
//
// David opens one screen, reads one number, makes one transfer in the bank,
// presses one button. He computes nothing and remembers nothing.
//
// Ready rows carry a stated rate and count toward the total. Held rows have
// no rate and sit below, visibly separated, excluded from the total, with an
// EMPTY rate field each (R1: learn, suggest, but don't populate). A held row
// never contributes zero to anything; while any row is held the total is
// provisional and says so.
//
// Confirm asks for the amount actually moved, typed. A mismatch is refused
// with both figures. See MoveConfirm.
//
// NIGHT 11 (docs/money/NIGHT-11.md). The queue is PER TRUST ACCOUNT (R23): a sweep moves money from
// one trust account to its sweep/income account, so the page shows one account's deposits at a time
// and the confirm names that account. A held row is rated by a RATE or by a DOLLAR the document states
// (R25), never both. The share is asked only where the carrier is co-brokered (R26); everywhere else it
// is 1.0 and the page does not raise it. Select all / deselect all / select settled are function, not polish.

const SHARE_OPTIONS = [
  { value: '', label: 'choose share' },
  { value: '1', label: 'full (1.0)' },
  { value: '0.5', label: 'co-brokered (0.5)' },
];

function HeldRow({ row, onSaved }) {
  // All three start EMPTY. Nothing here reads a prior rate, a carrier schedule or
  // another row (guard: tests/sweep-no-prefill.test.js).
  const [ratePct, setRatePct] = useState('');
  const [share, setShare] = useState('');
  const [dollars, setDollars] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const byDollar = dollars !== '';
    if (byDollar && ratePct !== '') { toast.error('State a rate or a dollar commission, not both.'); return; }
    let body;
    if (byDollar) {
      const d = Number(dollars);
      if (!Number.isFinite(d) || d < 0) { toast.error("Type the commission in dollars, as the binder states it."); return; }
      body = { commission_dollars: d };
    } else {
      const pct = Number(ratePct);
      if (ratePct === '' || !Number.isFinite(pct) || pct <= 0 || pct > 100) { toast.error('Rate is a percentage from the binder, e.g. 15 or 12.5, or type the dollar commission.'); return; }
      if (row.share_applies && share === '') { toast.error('This carrier is co-brokered: choose the share.'); return; }
      body = { commission_rate: pct / 100, ...(row.share_applies ? { commission_share: Number(share) } : {}) };
    }
    setBusy(true);
    try {
      await api.patch(`/api/sweep/${row.id}/rate`, body);
      toast.success(byDollar
        ? `Commission stated on ${row.counterparty || row.crm_deal_id}: ${fmtCents(Number(dollars))}.`
        : `Rate stated on ${row.counterparty || row.crm_deal_id}: ${ratePct}%${row.share_applies ? ` at ${share}` : ''}.`);
      await onSaved();
    } catch (e) {
      toast.error(e.body?.detail || e.message || 'Could not save the commission');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="grid gap-2 py-3 md:grid-cols-[1fr_auto] md:items-center">
      <RowFacts row={row} />
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-text-muted">
          Rate % (binder)
          <Input
            type="number"
            step="0.01"
            inputMode="decimal"
            data-money-input="commission_rate"
            value={ratePct}
            onChange={(e) => setRatePct(e.target.value)}
            className="mt-1 w-28"
          />
        </label>
        {row.share_applies ? (
          <label className="text-xs text-text-muted">
            Share
            <select
              data-money-input="commission_share"
              value={share}
              onChange={(e) => setShare(e.target.value)}
              className="mt-1 h-9 rounded-card border border-border bg-bg px-2 text-sm text-text"
            >
              {SHARE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        ) : null}
        <span className="pb-2 text-xs text-text-dim">or</span>
        <label className="text-xs text-text-muted">
          Commission $ (binder states it)
          <Input
            type="number"
            step="0.01"
            inputMode="decimal"
            data-money-input="commission_dollars"
            value={dollars}
            onChange={(e) => setDollars(e.target.value)}
            className="mt-1 w-32"
          />
        </label>
        <Button variant="outline" onClick={save} disabled={busy}>{busy ? 'Saving...' : 'State rate'}</Button>
      </div>
    </li>
  );
}

// THE CARRIER'S OWN FIGURE (David 2026-10-01). The rate derives a commission;
// on a half cent the carriers do not agree which way it goes, so the figure on
// their paper can be stated here. The server gates it to within one cent of
// what the rate derives: further than that is a different rate. The field
// starts EMPTY (guard: tests/sweep-no-prefill.test.js) and this page computes
// nothing; the refusal carries both figures.
function CommissionCorrection({ row, onSaved }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const v = Number(amount);
    if (amount === '' || !Number.isFinite(v)) { toast.error("Type the commission the carrier's paper shows."); return; }
    setBusy(true);
    try {
      await api.patch(`/api/sweep/${row.id}/rate`, { commission_rate: row.commission_rate, commission_share: row.commission_share, commission: v });
      toast.success(`Commission stated on ${row.counterparty || row.crm_deal_id}: ${fmtCents(v)}.`);
      setOpen(false);
      setAmount('');
      await onSaved(); // a confirmation is a read back, not an echo
    } catch (e) {
      toast.error(e.body?.detail || e.message || 'Could not state the commission');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <span className="text-[11px] text-text-dim">
        {row.carrier_differs ? <span className="mr-2 text-danger">carrier&apos;s statement shows commission {fmtCents(row.carrier_commission)}, this row {fmtCents(row.commission)}</span> : null}
        <button type="button" onClick={() => setOpen(true)} className="underline-offset-2 hover:text-text hover:underline">
          state the carrier&apos;s commission
        </button>
      </span>
    );
  }
  return (
    <div className="flex items-end gap-2">
      <label className="text-xs text-text-muted">
        Commission $ (the carrier&apos;s figure)
        <Input
          type="number"
          step="0.01"
          inputMode="decimal"
          data-money-input="commission_stated"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="mt-1 w-28"
        />
      </label>
      <Button variant="outline" onClick={save} disabled={busy}>{busy ? 'Saving...' : 'State commission'}</Button>
      <Button variant="ghost" onClick={() => { setOpen(false); setAmount(''); }} disabled={busy}>Cancel</Button>
    </div>
  );
}

function RowFacts({ row }) {
  return (
    <div className="min-w-0 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium text-text">{row.counterparty || 'counterparty not on file'}</span>
        <span className="font-mono text-xs text-text-dim">{row.reference || 'no reference'}</span>
        <span className="text-xs text-text-muted">{row.carrier_name || 'carrier not set'}</span>
        <a href={crmDeepLink('Deals', row.crm_deal_id)} target="_blank" rel="noreferrer" className="text-xs text-text-muted hover:underline">deal</a>
      </div>
      <div className="mt-0.5 font-mono text-[11.5px] text-text-muted">
        received {row.received_on || 'day unknown'} · gross {fmtCents(row.gross)} · premium {fmtCents(row.premium)} · tax {fmtCents(row.tax)} · fee {fmtCents(row.fee)}
        {row.commission_basis === 'dollar' ? <> · commission {fmtCents(row.commission)} <span className="text-warning">(stated as a dollar, no rate)</span></> : null}
        {row.commission_basis !== 'dollar' && row.commission_rate != null ? <> · rate {fmtPct(row.commission_rate)}{row.share_applies ? <> × {row.commission_share}</> : null} · commission {fmtCents(row.commission)}</> : null}
        {row.commission_basis === 'carrier_default' ? <span className="text-warning"> · carrier default (not the binder&apos;s rate; state the binder&apos;s rate or a dollar to replace it)</span> : null}
        {row.commission_stated ? <span className="text-warning"> · stated (the rate derives {fmtCents(row.commission_derived)})</span> : null}
        {row.missing && row.missing.length ? <span className="text-danger"> · {row.state === 'held' ? 'held: ' : ''}{row.missing.join(', ')}</span> : (row.held_reason ? <span className="text-danger"> · held: {row.held_reason}</span> : null)}
        {row.swept_before ? <span className="text-text-muted"> · {fmtCents(row.swept_before)} swept before; {fmtCents(row.sweep_amount)} left</span> : null}
        {row.receipt_mismatch ? <span className="text-danger"> · receipt differs from billed by {fmtCents(row.receipt_mismatch.difference)}</span> : null}
      </div>
    </div>
  );
}

export default function MoneySweep() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [params] = useSearchParams(); // Money this week's "Open the sweep" names the account
  const [accountId, setAccountId] = useState(params.get('account') || '');

  const refresh = useCallback(async () => {
    try {
      const d = await api.get(`/api/sweep/pending${accountId ? `?account_id=${encodeURIComponent(accountId)}` : ''}`);
      setData(d);
      setSelected(new Set(d.rows.filter((r) => r.state === 'ready').map((r) => r.id)));
      setError('');
    } catch (e) {
      setError(e.body?.code === 'ACCOUNT_NOT_TRUST' ? 'That is not a trust account.' : (e.message || 'Could not load the sweep queue'));
    }
  }, [accountId]);

  useEffect(() => { refresh(); }, [refresh]);

  const ready = useMemo(() => (data?.rows || []).filter((r) => r.state === 'ready'), [data]);
  const held = useMemo(() => (data?.rows || []).filter((r) => r.state === 'held'), [data]);
  const selectedRows = useMemo(
    () => Math.round(ready.filter((r) => selected.has(r.id)).reduce((acc, r) => acc + Number(r.sweep_amount), 0) * 100) / 100,
    [ready, selected],
  );
  // Ruling E (David 2026-11-01): what the sweep account owes back to this trust account comes off this sweep. The
  // server computes the owed figure; the page only applies it to the rows chosen, never more than they carry.
  const owedBack = Number(data?.owed_back || 0);
  const takeBack = Math.round(Math.max(0, Math.min(owedBack, selectedRows)) * 100) / 100;
  const selectedTotal = Math.round((selectedRows - takeBack) * 100) / 100;

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAll = () => setSelected(new Set(ready.map((r) => r.id)));
  const selectNone = () => setSelected(new Set());
  // Settled = the deposit sits inside the bank export loaded for this account (observed, not only reported).
  const selectSettled = () => setSelected(new Set(ready.filter((r) => r.settled_in_export).map((r) => r.id)));
  const destination = data?.destination?.name || 'the sweep account';

  const confirm = async ({ amount_moved, moved_on, override_reason }) => {
    try {
      const r = await api.post('/api/sweep/confirm', {
        transaction_ids: [...selected],
        account_id: data.account.id,
        amount_moved: Number(amount_moved),
        moved_on,
        override_reason,
      });
      toast.success(`Recorded: ${r.stamped.length} row(s) swept, ${fmtCents(r.moved)} on ${moved_on}${r.override ? ' (override)' : ''}.`);
      await refresh(); // a confirmation is a read back, not an echo
      return { ok: true };
    } catch (e) {
      if (e.status === 409 && e.body?.code === 'SWEEP_AMOUNT_MISMATCH') return { ok: false, mismatch: e.body };
      if (e.status === 409 && e.body?.code === 'SWEEP_ROWS_NOT_READY') {
        toast.error('Some selected rows are no longer ready. Reloading.');
        await refresh();
        return { ok: false };
      }
      if (e.status === 409 && (e.body?.code === 'WRONG_ACCOUNT' || e.body?.code === 'MIXED_ACCOUNTS' || e.body?.code === 'ACCOUNT_NOT_STATED')) {
        toast.error(e.body?.detail || 'These rows are not all in this trust account. Reloading.');
        await refresh();
        return { ok: false };
      }
      toast.error(e.body?.error || e.message || 'Could not record the sweep');
      return { ok: false };
    }
  };

  return (
    <div className="p-4">
      <div>
        <h2 className="text-xl font-semibold text-text">Sweep</h2>
        <p className="mt-1 text-sm text-text-muted">
          Settled deposits in {data?.account?.name || 'a trust account'} whose commission and fee have not moved to {destination}. Sweep by state, never by date.
        </p>
      </div>

      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {data === null && !error ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}

      {data ? (
        <>
          <div className="mt-3 flex flex-wrap gap-2" role="tablist" aria-label="Trust account">
            {data.accounts.map((a) => (
              <button
                key={a.id}
                type="button"
                role="tab"
                aria-selected={a.id === data.account?.id}
                onClick={() => setAccountId(a.id)}
                className={`rounded-card border px-3 py-1.5 text-sm ${a.id === data.account?.id ? 'border-accent bg-accent text-white' : 'border-border bg-surface text-text'}`}
              >
                {a.name} · {a.open_rows}
              </button>
            ))}
          </div>
          {data.unattributed_count > 0 ? (
            <p className="mt-2 text-xs text-danger">{data.unattributed_count} deposit(s) name no trust account and are in no queue. State the account on the Trust page.</p>
          ) : null}
          {data.paid_gross_to_wholesaler_count > 0 ? (
            <p className="mt-2 text-xs text-text-muted">{data.paid_gross_to_wholesaler_count} deposit(s) were paid in full to a wholesaler and are not offered here.</p>
          ) : null}

          <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-sm">
            <span className="text-text">Ready {data.ready_count} · {fmtCents(data.ready_total)}</span>
            {owedBack > 0 ? <span className="text-warning">Less owed back to {data.account?.name || 'this account'} from an earlier sweep: {fmtCents(takeBack)}{takeBack < owedBack ? ` of ${fmtCents(owedBack)}` : ''} · to move {fmtCents(selectedTotal)}</span> : null}
            <span className="text-text-muted">Held {data.held_count} · gross {fmtCents(data.held_gross)}</span>
            {data.amount_provisional ? <span className="text-danger">Provisional: {data.held_count} row(s) carry no commission and are excluded from the total</span> : null}
          </div>

          <section className="mt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-text-dim">Ready</h3>
              {ready.length > 0 ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={selectAll}>Select all</Button>
                  <Button size="sm" variant="outline" onClick={selectNone}>Deselect all</Button>
                  {data.export_through ? <Button size="sm" variant="outline" onClick={selectSettled} title={`Deposits received on or before ${data.export_through}, the last day of the bank export loaded for this account`}>Select all settled</Button> : null}
                </div>
              ) : null}
            </div>
            {ready.length === 0 ? <p className="mt-2 text-sm text-text-muted">Nothing ready to sweep.</p> : (
              <ul className="mt-2 divide-y divide-border rounded-card border border-border bg-surface px-3">
                {ready.map((row) => (
                  <li key={row.id} className="flex items-start gap-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggle(row.id)}
                      className="mt-1 h-4 w-4"
                      aria-label={`include ${row.counterparty || row.crm_deal_id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <RowFacts row={row} />
                      {row.commission_basis === 'dollar' ? null : <div className="mt-1"><CommissionCorrection row={row} onSaved={refresh} /></div>}
                    </div>
                    <div className="shrink-0 font-mono text-sm text-text">{fmtCents(row.sweep_amount)}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {data.short ? (
            <p className="mt-4 rounded-card border border-danger/40 bg-surface p-3 text-sm text-danger">{data.short.why} The ready rows stay listed; no sweep is proposed.</p>
          ) : null}
          <div className="mt-4">
            <MoveConfirm
              computedTotal={selectedTotal}
              count={selected.size}
              verb="sweep"
              destination={destination}
              disabled={ready.length === 0 || data.proposes_sweep === false}
              onConfirm={confirm}
            />
          </div>

          <section className="mt-8 border-t border-border pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-text-dim">Held: no commission stated</h3>
            <p className="mt-1 text-xs text-text-muted">
              A deposit whose commission is not confirmed stays in the trust account. Type the rate off the binder, or the dollar commission when the binder states one; nothing is suggested.
            </p>
            {held.length === 0 ? <p className="mt-2 text-sm text-text-muted">Nothing held.</p> : (
              <ul className="mt-2 divide-y divide-border rounded-card border border-border bg-surface px-3">
                {held.map((row) => <HeldRow key={row.id} row={row} onSaved={refresh} />)}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
